import {
  SENDIG_DOMAINS,
  sendDomainLabel,
  SUPPLEMENTAL_MODEL,
  supplementalLabel,
} from '@/components/Agents/SendValidator/utils/SendDomains';
import { _isNil, _isNotBlank, _notNil } from '@/littledash';
import type { SendFileDetection } from '@/model/Agent.model';
import { countOf, formatCount } from './SendText';

/**
 * The confirm step's per-file domain picker, decided from what the server's detection published. The
 * server decides every refusal (the confirm request is refused for each of these too); this only lets
 * the screen say so before submitting, using the server's own facts and lists, never a copy of them.
 */

export interface SendDomainOption {
  label: string;
  value: string;
}

export interface SendDomainOptionGroup {
  label: string;
  options: Array<SendDomainOption>;
}

/** Whether detection could tell which domain the file is; a detection stored before it said so could. */
export const isUndetermined = (detection: SendFileDetection): boolean =>
  detection.detection_state === 'undetermined' || !_isNotBlank(detection.detected_domain);

const optionLabel = (code: string, supplementalDataset: string | undefined): string =>
  code === SUPPLEMENTAL_MODEL ? supplementalLabel(supplementalDataset) : sendDomainLabel(code);

/**
 * The picker's options for one file: the ranked candidates first, best first, then every other domain
 * for an override. A candidate is called the best match only when detection determined it; on a file
 * detection could not tell, every candidate is a possible match, so no marginal winner reads as certain.
 */
export const sendDomainOptionGroupsFor = (
  detection: SendFileDetection,
  supplementalDataset: string | undefined
): Array<SendDomainOptionGroup> => {
  // Only a detection that says it determined the domain names a best match. One stored before
  // detection said so may hold a pick nobody determined, so its candidates are all possible matches.
  const determined = detection.detection_state === 'determined';
  const ranked = detection.candidates.reduce<Array<SendDomainOption>>((options, candidate) => {
    if (SENDIG_DOMAINS.some((domain) => domain.code === candidate.domain_code)) {
      const best = determined && candidate.domain_code === detection.detected_domain;
      options.push({
        label: `${optionLabel(candidate.domain_code, supplementalDataset)} (${best ? 'best match' : 'possible match'})`,
        value: candidate.domain_code,
      });
    }
    return options;
  }, []);
  const rankedCodes = new Set(ranked.map((option) => option.value));
  const others = SENDIG_DOMAINS.reduce<Array<SendDomainOption>>((options, domain) => {
    if (!rankedCodes.has(domain.code)) {
      options.push({ label: optionLabel(domain.code, supplementalDataset), value: domain.code });
    }
    return options;
  }, []);
  return [
    ...(ranked.length > 0 ? [{ label: 'Ranked matches', options: ranked }] : []),
    { label: 'All domains', options: others },
  ];
};

/** The domain the file's own DOMAIN column names in every row, when it is the one chosen: evidence to show. */
export const domainColumnEvidenceFor = (
  detection: SendFileDetection,
  chosen: string | undefined
): string | undefined => {
  const named = detection.domain_column?.named_domain;
  if (_isNil(named) || named !== chosen) {
    return undefined;
  }
  const said = detection.domain_column?.values[0]?.value ?? named;
  return `This file's own DOMAIN column says ${said}.`;
};

/** Strings by code unit, as the server orders them, so a tie in rows lists the values the same way. */
const codeOrder = (left: string, right: string): number => (left < right ? -1 : left > right ? 1 : 0);

/**
 * Why a file whose DOMAIN column names more than one domain can't be confirmed as any one of them: each
 * value with the rows holding it, most rows first, so a single stray value reads as what it is. The
 * same words as the server's refusal.
 */
export const mixedDomainRefusalOf = (detection: SendFileDetection): string | undefined => {
  const column = detection.domain_column;
  if (_isNil(column) || !column.mixed) {
    return undefined;
  }
  const parts = [...column.values]
    .sort((left, right) => (right.rows !== left.rows ? right.rows - left.rows : codeOrder(left.value, right.value)))
    .map((value) => `"${value.value}" in ${countOf(value.rows, 'row', 'rows')}`);
  const listed = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  const values = column.values_omitted ? `${listed}, and other values` : listed;
  return `${detection.original_filename} holds more than one domain: its DOMAIN column reads ${values}. A file must be one domain: correct a value that is wrong, or split the file into one file per domain, and upload it again.`;
};

/** Why a file can't be confirmed as a domain it holds too many rows for, naming the cap. */
export const rowCapRefusalOf = (detection: SendFileDetection, chosen: string | undefined): string | undefined => {
  const cap = detection.over_row_cap?.find((over) => over.domain_code === chosen);
  if (_isNil(cap) || _isNil(detection.row_count)) {
    return undefined;
  }
  return `${detection.original_filename} has ${countOf(detection.row_count, 'row', 'rows')}, more than the ${formatCount(cap.row_cap)} a ${chosen} file may hold in this validator. Check that it is the study's ${chosen}, and confirm it as the domain it is.`;
};

/**
 * Which files are chosen as each dataset a study has only one of, from the server's list, keyed by
 * dataset. Supplemental datasets are never on it, so files chosen as SUPPQUAL never appear.
 */
export const singleInstanceChoices = (
  chosenByFile: ReadonlyArray<{ attachmentApiId: string; filename: string; chosen: string | undefined }>,
  singleInstanceDatasets: ReadonlyArray<string>
): Map<string, Array<{ attachmentApiId: string; filename: string }>> =>
  chosenByFile.reduce<Map<string, Array<{ attachmentApiId: string; filename: string }>>>((byDataset, file) => {
    if (_notNil(file.chosen) && singleInstanceDatasets.includes(file.chosen)) {
      byDataset.set(file.chosen, [
        ...(byDataset.get(file.chosen) ?? []),
        { attachmentApiId: file.attachmentApiId, filename: file.filename },
      ]);
    }
    return byDataset;
  }, new Map());

/** The single-instance datasets another file is already chosen as, which this file is not offered. */
export const takenByOtherFiles = (
  choices: Map<string, Array<{ attachmentApiId: string }>>,
  attachmentApiId: string
): Set<string> =>
  [...choices].reduce<Set<string>>((taken, [dataset, files]) => {
    if (files.some((file) => file.attachmentApiId !== attachmentApiId)) {
      taken.add(dataset);
    }
    return taken;
  }, new Set());

/** Why a file can't be confirmed as a dataset another file is also chosen as. */
export const singleInstanceRefusalOf = (dataset: string, filenames: ReadonlyArray<string>): string =>
  `A study has one ${dataset}, but ${filenames.length} files are chosen as ${dataset}: ${[...filenames].sort().join(', ')}. Choose the domain each file is.`;
